import React, { useState, useEffect } from 'react';
import { PlusIcon, DocumentIcon, FolderIcon } from '@heroicons/react/24/outline';
import TemplateEditor from '../../components/Templates/TemplateEditor';
import { templateService } from '../../services/templateService';
import { Template } from '../../types/templates';
import Notification from '../../components/UI/Notification';
import ConfirmationDialog from '../../components/UI/ConfirmationDialog';
import { Button } from '../../components/UI/Button';

const TemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    type: 'success' | 'error';
    message: string;
  }>({
    show: false,
    type: 'success',
    message: '',
  });
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    template: Template | null;
  }>({
    isOpen: false,
    template: null,
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({
      show: true,
      type,
      message,
    });
    setTimeout(() => {
      setNotification({ show: false, type: 'success', message: '' });
    }, 3000);
  };

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const templates = await templateService.getTemplates(false); // Não incluir o modelo "empty"
      setTemplates(templates);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      showNotification('error', 'Erro ao carregar modelos.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setIsEditorOpen(true);
  };

  const handleEditTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setIsEditorOpen(true);
  };

  const handleDeleteTemplate = async (template: Template) => {
    setConfirmationDialog({
      isOpen: true,
      template,
    });
  };

  const handleConfirmDelete = async () => {
    if (!confirmationDialog.template) return;

    try {
      await templateService.deleteTemplate(confirmationDialog.template.id);
      setTemplates(templates.filter(t => t.id !== confirmationDialog.template?.id));
      showNotification('success', 'Modelo excluído com sucesso.');
    } catch (error) {
      console.error('Erro ao excluir template:', error);
      showNotification('error', 'Erro ao excluir modelo.');
    } finally {
      setConfirmationDialog({ isOpen: false, template: null });
    }
  };

  const handleSaveTemplate = async (templateData: Partial<Template>) => {
    try {
      if (templateData.id) {
        // Atualização
        await templateService.updateTemplate(templateData.id, templateData);
        setTemplates(templates.map(t => 
          t.id === templateData.id 
            ? { ...t, ...templateData }
            : t
        ));
        showNotification('success', 'Modelo atualizado com sucesso.');
      } else {
        // Criação
        const newTemplate = await templateService.createTemplate({
          name: templateData.name || '',
          description: templateData.description || '',
          content: templateData.content || '',
          isActive: templateData.isActive ?? true
        });
        setTemplates([...templates, newTemplate]);
        showNotification('success', 'Modelo criado com sucesso.');
      }
      setIsEditorOpen(false);
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      showNotification('error', 'Erro ao salvar modelo.');
    }
  };

  return (
    <div className="flex-1">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Modelos de Documentos</h1>
        <p className="text-gray-600">Gerencie seus modelos de documentos eletrônicos.</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <Button
            onClick={handleCreateTemplate}
            variant="primary"
            size="md"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Novo Modelo
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <DocumentIcon className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum modelo encontrado</h3>
            <p className="text-gray-500 mb-4 max-w-md">
              Você ainda não possui modelos de documentos. Crie seu primeiro modelo para começar a trabalhar com documentos padronizados.
            </p>
            <Button
              onClick={handleCreateTemplate}
              variant="primary"
              size="md"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Criar modelo
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {templates.map((template) => (
              <div key={template.id} className="p-4 hover:bg-gray-50 transition-colors duration-150">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <DocumentIcon className="h-6 w-6 text-gray-400 mr-3" />
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {template.name}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {template.description}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <p>
                        Atualizado em: {new Date(template.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0 flex space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleEditTemplate(template)}
                      title="Editar modelo"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteTemplate(template)}
                      title="Excluir modelo"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isEditorOpen && (
        <TemplateEditor
          isOpen={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          template={selectedTemplate}
          onSave={handleSaveTemplate}
        />
      )}

      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onClose={() => setConfirmationDialog({ isOpen: false, template: null })}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este modelo? Esta ação não poderá ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      <Notification
        show={notification.show}
        type={notification.type}
        message={notification.message}
        onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
      />
    </div>
  );
};

export default TemplatesPage; 